/**
 * i18n Configuration
 * Central configuration for internationalization system
 * Uses constants to avoid hardcoding values
 */

import { STORAGE_KEYS } from '../constants/storage';

// Storage key for localStorage (exported for backward compatibility)
export const STORAGE_KEY = STORAGE_KEYS.LANGUAGE;

// Test key for localStorage availability check
export const STORAGE_TEST_KEY = '__asgard_storage_test__';

// Default fallback language
export const DEFAULT_LANGUAGE = 'en';

// Cache configuration
export const MAX_CACHE_SIZE = 10; // Maximum number of languages to cache

// Language code validation constants
export const MIN_LANGUAGE_CODE_LENGTH = 2;
export const MAX_LANGUAGE_CODE_LENGTH = 20;

// Top 20 languages to preload (ordered by speakers)
export const TOP_20_LANGUAGE_CODES = [
  'en',
  'zh',
  'hi',
  'es',
  'fr',
  'ar',
  'bn',
  'pt',
  'ru',
  'ja',
  'de',
  'ko',
  'vi',
  'te',
  'mr',
  'ta',
  'tr',
  'it',
  'ur',
  'th',
];

// RTL languages
export const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur', 'iw']; // iw is alternate code for Hebrew

// Supported languages (125 Google Translate languages)
// This will be populated from GoogleTranslateLanguageSelector data
export const SUPPORTED_LANGUAGES = [
  'en',
  'zh',
  'hi',
  'es',
  'fr',
  'ar',
  'bn',
  'pt',
  'pt-br', // Brazilian Portuguese (regional variant)
  'ru',
  'ja',
  'de',
  'ko',
  'vi',
  'te',
  'mr',
  'ta',
  'tr',
  'it',
  'ur',
  'th',
  'af',
  'am',
  'ha',
  'ig',
  'ny',
  'rw',
  'sn',
  'so',
  'st',
  'sw',
  'xh',
  'yo',
  'zu',
  'as',
  'ay',
  'bm',
  'bho',
  'dv',
  'doi',
  'ee',
  'gu',
  'id',
  'ilo',
  'jv',
  'kn',
  'kk',
  'km',
  'ku',
  'ky',
  'lo',
  'mai',
  'ml',
  'mni-mtei',
  'mn',
  'my',
  'ne',
  'or',
  'om',
  'ps',
  'pa',
  'sa',
  'sd',
  'si',
  'su',
  'ti',
  'tl',
  'tk',
  'ug',
  'uz',
  'sq',
  'eu',
  'be',
  'bs',
  'bg',
  'ca',
  'ceb',
  'co',
  'hr',
  'cs',
  'da',
  'nl',
  'et',
  'fi',
  'fy',
  'gl',
  'ka',
  'el',
  'gn',
  'ht',
  'haw',
  'iw',
  'he',
  'hu',
  'is',
  'ga',
  'kri',
  'la',
  'lv',
  'ln',
  'lt',
  'lg',
  'lb',
  'mk',
  'mg',
  'ms',
  'mt',
  'mi',
  'no',
  'pl',
  'qu',
  'ro',
  'sm',
  'gd',
  'sr',
  'sk',
  'sl',
  'sv',
  'tg',
  'tt',
  'ts',
  'cy',
  'yi',
  'az',
  'fa',
  'hy',
  'uk',
];

// Translation file path pattern
// Use absolute path for web (serverless mode) to support SPA routing
// Use relative path for Electron (file:// protocol)
const isServerlessMode = import.meta.env.VITE_API_MODE === 'serverless';
export const TRANSLATION_PATH_PATTERN = isServerlessMode
  ? '/locales/{lang}.json'
  : './locales/{lang}.json';

// Performance thresholds (in milliseconds)
export const PERF_THRESHOLD_INITIAL_LOAD = 100;
export const PERF_THRESHOLD_LANGUAGE_SWITCH = 200;
export const PERF_THRESHOLD_TRANSLATION_LOOKUP = 1;
export const PERF_THRESHOLD_CACHED_LOAD = 5;

// Development mode flag
export const IS_DEV_MODE = import.meta.env.DEV;

// Auto language detection disabled - English only for now, browser translation handles localization
// Set VITE_ENABLE_AUTO_LANGUAGE_DETECTION=true to re-enable when i18n translations are improved
export const ENABLE_AUTO_LANGUAGE_DETECTION = false;

// Storage version for migration
export const STORAGE_VERSION = '1.0';
export const STORAGE_VERSION_KEY = STORAGE_KEYS.LANGUAGE_VERSION;

// Pinned languages (always shown at top of language selector)
export const PINNED_LANGUAGE_CODES = ['en', 'zh', 'es'] as const;

// Auto-detected language storage key
export const AUTO_DETECTED_LANGUAGE_KEY = STORAGE_KEYS.LANGUAGE_AUTO_DETECTED;
